import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  App,
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Switch,
  Typography
} from 'antd';
import type { LabeledValue } from 'antd/lib/select';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { _debounce } from '@medicusunion/front-utils';
import {
  createInvoice,
  fetchInvoice,
  previewInvoicePdf,
  reissueInvoice,
  updateDraftInvoice
} from 'services/api/invoicing';
import { fetchInvoicingProducts } from 'services/api/invoicingProducts';
import { fetchBillingAddresses } from 'services/api/invoicingBillingAddresses';
import {
  INVOICE_LANGUAGE_OPTIONS,
  INVOICE_PAYMENT_METHOD_OPTIONS,
  INVOICE_TAX_MODE_OPTIONS
} from 'constants/invoicing';
import { fetchCustomers } from 'services/api/invoicingCustomers';
import {
  BillingAddress,
  CreateInvoiceInput,
  Customer,
  Invoice,
  InvoiceDiscountType,
  InvoiceLanguage,
  InvoiceLine,
  InvoicePaymentMethod,
  InvoiceServiceType,
  InvoiceStatus,
  InvoiceTaxMode,
  InvoicingProduct,
  PartyKind,
  PartyResult
} from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { Loading, PageWrapper } from 'components/ui';
import AddressModal from '../BillingAddresses/components/AddressModal';
import LineItems from './components/LineItems';
import CustomerModal from './components/CustomerModal';
import PartySelect from './components/PartySelect';
import { customerAddressSummary, customerName } from '../customerDisplay';

const debouncePreview = _debounce();

type LineForm = {
  productId?: string;
  lineType?: InvoiceServiceType;
  descriptionEn?: string;
  descriptionDe?: string;
  doctor?: LabeledValue; // { value: doctorId, label: doctorName }
  quantity?: number;
  unitAmount?: number;
  taxRate?: number;
  discountType?: InvoiceDiscountType;
  discountValue?: number;
};

type FormValues = {
  patientId?: number | null;
  patientName?: string;
  patientEmail?: string;
  billingAddressId?: number | null;
  language?: InvoiceLanguage;
  currency?: string;
  paymentMethod?: InvoicePaymentMethod;
  taxMode?: InvoiceTaxMode;
  taxBaseAmount?: number;
  taxBaseRate?: number;
  dueDate?: Dayjs | null;
  issue?: boolean;
  /** Reissue only: why the original is being voided. */
  reason?: string;
  lines?: LineForm[];
};

const buildInput = (
  values: FormValues,
  customer?: Customer | null
): CreateInvoiceInput => {
  const common = {
    language: values.language,
    // Currency omitted: the BE fixes it to EUR (platform bills EUR only).
    paymentMethod: values.paymentMethod,
    taxMode: values.taxMode,
    taxBaseAmount:
      values.taxMode === InvoiceTaxMode.BaseOverride
        ? values.taxBaseAmount ?? undefined
        : undefined,
    taxBaseRate:
      values.taxMode === InvoiceTaxMode.BaseOverride
        ? values.taxBaseRate ?? undefined
        : undefined,
    dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : undefined,
    issue: values.issue,
    lines: (values.lines ?? []).map(line => {
      const discountValue =
        line.discountValue != null && Number(line.discountValue) > 0
          ? Number(line.discountValue)
          : undefined;

      return {
        lineType: line.lineType || undefined,
        productId: line.productId || undefined,
        doctorId:
          line.doctor?.value != null ? Number(line.doctor.value) : undefined,
        doctorName:
          typeof line.doctor?.label === 'string'
            ? line.doctor.label
            : undefined,
        descriptionEn: line.descriptionEn?.trim() || undefined,
        descriptionDe: line.descriptionDe?.trim() || undefined,
        quantity: Number(line.quantity) || 0,
        unitAmount: Number(line.unitAmount) || 0,
        taxRate: line.taxRate != null ? Number(line.taxRate) : undefined,
        discountType: discountValue
          ? line.discountType || 'percent'
          : undefined,
        discountValue
      };
    })
  };

  // Customer mode: the BE snapshots the customer's own billing address and
  // ignores patientId when customerId is set.
  if (customer) {
    return {
      ...common,
      customerId: customer.id,
      patientName: customerName(customer),
      patientEmail: customer.email || undefined
    };
  }

  return {
    ...common,
    patientId: values.patientId ?? undefined,
    patientName: (values.patientName ?? '').trim(),
    patientEmail: values.patientEmail?.trim() || undefined,
    billingAddressId: values.billingAddressId ?? undefined
  };
};

/**
 * The saved draft, back into the form it was written in.
 *
 * EVERY MONEY FIELD ARRIVES AS A STRING. quantity, unitAmount, taxRate
 * and discountValue are all `string` on the wire while the form holds
 * numbers, so each one is coerced here rather than trusted: the symptom
 * of missing it is not a crash but "350" + 50 becoming "35050", a total
 * that is wrong and plausible at the same time.
 *
 * Nulls become undefined, because antd treats null as a value to render
 * and undefined as "nothing chosen" — a null discountType paints the
 * select as filled with blank.
 */
const toFormValues = (invoice: Invoice, lines: InvoiceLine[]): FormValues => ({
  patientId: invoice.patientId ?? undefined,
  patientName: invoice.patientName ?? undefined,
  patientEmail: invoice.patientEmail ?? undefined,
  billingAddressId: invoice.billingAddressId ?? undefined,
  language: invoice.language,
  currency: invoice.currency,
  paymentMethod: invoice.paymentMethod,
  taxMode: invoice.taxMode,
  taxBaseAmount:
    invoice.taxBaseAmount != null ? Number(invoice.taxBaseAmount) : undefined,
  taxBaseRate:
    invoice.taxBaseRate != null ? Number(invoice.taxBaseRate) : undefined,
  dueDate: invoice.dueDate ? dayjs(invoice.dueDate) : undefined,
  lines: lines.map(line => ({
    productId: line.productId ?? undefined,
    lineType: line.lineType,
    descriptionEn: line.descriptionEn ?? undefined,
    descriptionDe: line.descriptionDe ?? undefined,
    doctor:
      line.doctorId != null
        ? { value: line.doctorId, label: line.doctorName ?? '' }
        : undefined,
    quantity: Number(line.quantity),
    unitAmount: Number(line.unitAmount),
    taxRate: Number(line.taxRate),
    // A zero discount is "no discount": keeping the type without a value
    // would paint a discount control the invoice does not have.
    discountType: Number(line.discountValue ?? 0)
      ? line.discountType ?? undefined
      : undefined,
    discountValue: Number(line.discountValue ?? 0) || undefined
  }))
});

const CreateInvoice: FC = () => {
  const { publicId: editingRef } = useParams();
  const { pathname } = useLocation();
  /**
   * One screen, three jobs: create, edit a draft, and reissue an issued
   * invoice. They share every field and the same payload builder — the
   * differences are which endpoint takes it, and that a reissue explains
   * itself.
   */
  const isReissue = pathname.endsWith('/reissue');
  const isEditing = !!editingRef && !isReissue;
  const [loadingDraft, setLoadingDraft] = useState(!!editingRef);
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const [products, setProducts] = useState<InvoicingProduct[]>([]);
  // `kind:ref` of the picked party — see PartySelect for why it is namespaced.
  const [partyValue, setPartyValue] = useState<string | undefined>();
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(
    null
  );

  const [billingAddresses, setBillingAddresses] = useState<BillingAddress[]>(
    []
  );

  const [addressModalOpen, setAddressModalOpen] = useState(false);

  // Non-user billing customer (mutually exclusive with a patient).
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  // When set, the customer modal opens in edit mode for this customer.
  const [customerEditing, setCustomerEditing] = useState<Customer | null>(null);
  // Ref so the debounced preview reads the latest customer without recreating
  // schedulePreview (which loadBillingAddresses depends on).
  const customerRef = useRef<Customer | null>(null);
  customerRef.current = customer;

  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const taxMode = Form.useWatch('taxMode', form);

  // active catalog products (for line prefill). Returns the fresh list so an
  // inline-created product can be selected right after it's added.
  const reloadProducts = useCallback(async (): Promise<InvoicingProduct[]> => {
    const list = await fetchInvoicingProducts(true);
    setProducts(list);

    return list;
  }, []);

  useEffect(() => {
    reloadProducts().catch(() => undefined);
  }, [reloadProducts]);

  const schedulePreview = useCallback(() => {
    debouncePreview(async () => {
      // The BE renders the default issuer template even with empty customer /
      // zero lines, so we always preview — this is what shows the admin the
      // default invoice template as soon as the screen opens.
      const input = buildInput(form.getFieldsValue(true), customerRef.current);

      try {
        setPreviewLoading(true);
        const blob = await previewInvoicePdf(input);
        setPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);

          return URL.createObjectURL(blob);
        });
      } catch {
        // transient/invalid state — keep the previous preview
      } finally {
        setPreviewLoading(false);
      }
    });
  }, [form]);

  // Show the default invoice template immediately on open (before any input).
  useEffect(() => {
    schedulePreview();
  }, [schedulePreview]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  // Load a patient's billing addresses. `selectId` auto-selects an address
  // after a reload (used after creating one inline) — the default, else the
  // newest. Returns nothing; it drives the Select via state.
  const loadBillingAddresses = useCallback(
    async (patientId: number, selectId?: number) => {
      try {
        const addresses = await fetchBillingAddresses(patientId);
        setBillingAddresses(addresses);

        if (selectId != null) {
          form.setFieldValue('billingAddressId', selectId);
          schedulePreview();
        }
      } catch {
        setBillingAddresses([]);
      }
    },
    [form, schedulePreview]
  );

  const selectPatientParty = (party: PartyResult) => {
    const id = Number(party.ref);

    setCustomer(null);
    form.setFieldsValue({
      patientId: id,
      patientName: `${party.firstName} ${party.lastName ?? ''}`.trim(),
      patientEmail: party.email ?? undefined
    });
    setSelectedPatientId(id);
    loadBillingAddresses(id);
  };

  /**
   * A customer party carries only the picker fields, but the summary and the
   * Edit button need the whole row — look it up and fall back to a stub built
   * from the party, whose `ref` the BE accepts as `customerId` either way.
   */
  const selectCustomerParty = async (party: PartyResult) => {
    let full: Customer | null = null;

    try {
      const rows = await fetchCustomers(
        party.email || `${party.firstName} ${party.lastName ?? ''}`.trim()
      );

      full = rows.find(row => row.publicId === party.ref) ?? null;
    } catch {
      // fall through to the stub — the invoice can still be raised
    }

    handleCustomerSelected(
      full ?? {
        id: party.ref,
        publicId: party.ref,
        firstName: party.firstName,
        lastName: party.lastName,
        email: party.email,
        phone: party.phone,
        dateOfBirth: party.dateOfBirth,
        gender: party.gender,
        company: null,
        vatId: null,
        line1: null,
        line2: null,
        city: null,
        state: null,
        postalCode: null,
        country: null
      }
    );
  };

  const handlePartySelect = (party: PartyResult | null) => {
    if (!party) {
      form.setFieldValue('patientId', undefined);
      setSelectedPatientId(null);
      setBillingAddresses([]);
      form.setFieldValue('billingAddressId', undefined);
      schedulePreview();

      return;
    }

    if (party.kind === PartyKind.Customer) {
      selectCustomerParty(party);

      return;
    }

    selectPatientParty(party);
    form.setFieldValue('billingAddressId', undefined);
    schedulePreview();
  };

  // Bill a non-user customer: clears the patient selection and its address book.
  const handleCustomerSelected = (picked: Customer) => {
    setCustomer(picked);
    setPartyValue(undefined);
    setSelectedPatientId(null);
    setBillingAddresses([]);
    form.setFieldsValue({
      patientId: undefined,
      billingAddressId: undefined,
      patientName: customerName(picked),
      patientEmail: picked.email ?? undefined
    });
    schedulePreview();
  };

  const clearCustomer = () => {
    setCustomer(null);
    form.setFieldsValue({ patientName: undefined, patientEmail: undefined });
    schedulePreview();
  };

  const openCustomerCreate = () => {
    setCustomerEditing(null);
    setCustomerModalOpen(true);
  };

  const openCustomerEdit = () => {
    setCustomerEditing(customer);
    setCustomerModalOpen(true);
  };

  const closeCustomerModal = () => {
    setCustomerModalOpen(false);
    setCustomerEditing(null);
  };

  // A newly created address is the default (isDefault), else the newest id.
  const handleAddressCreated = () => {
    if (selectedPatientId == null) {
      return;
    }

    fetchBillingAddresses(selectedPatientId)
      .then(addresses => {
        setBillingAddresses(addresses);

        const picked =
          addresses.find(addr => addr.isDefault) ??
          addresses.reduce<BillingAddress | null>(
            (newest, addr) =>
              !newest || Number(addr.id) > Number(newest.id) ? addr : newest,
            null
          );

        if (picked) {
          form.setFieldValue('billingAddressId', Number(picked.id));
          schedulePreview();
        }
      })
      .catch(() => undefined);
  };

  /**
   * Load the draft being edited.
   *
   * A NON-DRAFT IS SENT BACK rather than shown in a form it cannot save:
   * the API refuses PUT on anything numbered, so an editor opened over an
   * issued invoice is a screen whose only outcome is a 400.
   */
  useEffect(() => {
    if (!editingRef) return;

    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchInvoice(editingRef);

        if (cancelled) return;

        // A reissue REPLACES something that has a number, so the one
        // status it cannot start from is draft — that one is simply
        // edited. Paid is refused by the API too: money has moved, and
        // that is the credit note's case, not this one.
        if (isReissue && res.invoice.status !== InvoiceStatus.Issued) {
          message.warning(
            res.invoice.status === InvoiceStatus.Paid
              ? 'A paid invoice is corrected with a credit note, not by reissuing it.'
              : 'Only an issued invoice can be reissued.'
          );
          navigate(`/invoicing/invoices/${editingRef}`, { replace: true });

          return;
        }

        if (!isReissue && res.invoice.status !== InvoiceStatus.Draft) {
          message.warning(
            'Only a draft can be edited. An issued invoice is corrected by reissuing it.'
          );
          navigate(`/invoicing/invoices/${editingRef}`, { replace: true });

          return;
        }

        form.setFieldsValue(toFormValues(res.invoice, res.lines));
        setSelectedPatientId(res.invoice.patientId ?? null);
      } catch (err) {
        if (!cancelled) {
          showErrorMessage(err);
          navigate('/invoicing/invoices', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRef]);

  const onFinish = async (values: FormValues) => {
    const input = buildInput(values, customer);

    // An invoice must be billed to a patient or a customer (BE enforces this).
    if (!customer && values.patientId == null) {
      message.error('Select a patient or create a customer to bill.');

      return;
    }

    if (!input.lines.length) {
      message.error('Add at least one line item');

      return;
    }

    try {
      setSaving(true);

      if (isReissue && editingRef) {
        const replacement = { ...input };

        delete (replacement as Partial<CreateInvoiceInput>).issue;

        const res = await reissueInvoice(editingRef, {
          ...replacement,
          reason: values.reason?.trim() || undefined
        });

        message.success('Invoice reissued');
        navigate(`/invoicing/invoices/${res.data.invoice.publicId}`);

        return;
      }

      if (isEditing && editingRef) {
        // `issue` is dropped rather than sent false: it is not on
        // UpdateInvoiceDto at all, and forbidNonWhitelisted turns an
        // unknown key into a 400 rather than ignoring it.
        const updates = { ...input };

        delete (updates as Partial<CreateInvoiceInput>).issue;

        await updateDraftInvoice(editingRef, updates);
        message.success('Draft saved');
        navigate(`/invoicing/invoices/${editingRef}`);

        return;
      }

      const res = await createInvoice(input);

      if (res.payUrl) {
        modal.success({
          title: 'Invoice created',
          content: (
            <span>
              Payment link:{' '}
              <a href={res.payUrl} target="_blank" rel="noreferrer">
                {res.payUrl}
              </a>
            </span>
          ),
          onOk: () => navigate('/invoicing/invoices')
        });
      } else {
        message.success('Invoice created');
        navigate('/invoicing/invoices');
      }
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setSaving(false);
    }
  };

  const billingOptions = useMemo(
    () =>
      billingAddresses.map(addr => ({
        value: Number(addr.id),
        label: [addr.line1, addr.city, addr.country].filter(Boolean).join(', ')
      })),
    [billingAddresses]
  );

  const billingPlaceholder = !selectedPatientId
    ? 'Select a patient first'
    : billingOptions.length
    ? 'Select an address'
    : 'No saved address — add one';

  return (
    <PageWrapper
      title={
        isReissue ? 'Reissue invoice' : isEditing ? 'Edit draft' : 'New invoice'
      }
      color="white"
      extra={[
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={() => form.submit()}
        >
          {isReissue
            ? 'Void and reissue'
            : isEditing
            ? 'Save draft'
            : 'Save invoice'}
        </Button>
      ]}
    >
      {loadingDraft ? (
        <Loading />
      ) : (
        <Row gutter={24}>
          <Col xs={24} lg={14}>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={schedulePreview}
              initialValues={{
                language: InvoiceLanguage.En,
                currency: 'EUR',
                paymentMethod: InvoicePaymentMethod.Stripe,
                taxMode: InvoiceTaxMode.PerLine,
                issue: true,
                lines: [{ quantity: 1, unitAmount: 0, discountType: 'percent' }]
              }}
            >
              <Typography.Title level={5}>Customer</Typography.Title>
              {customer ? (
                <Form.Item label="Billing customer">
                  <Row gutter={8} align="middle" wrap={false}>
                    <Col flex="auto">
                      <Typography.Text strong>
                        {customerName(customer)}
                      </Typography.Text>
                      {customer.email ? (
                        <Typography.Text type="secondary">
                          {` · ${customer.email}`}
                        </Typography.Text>
                      ) : null}
                      <br />
                      <Typography.Text type="secondary">
                        {customerAddressSummary(customer)}
                      </Typography.Text>
                    </Col>
                    <Col flex="none">
                      <Button onClick={openCustomerEdit}>Edit</Button>
                    </Col>
                    <Col flex="none">
                      <Button onClick={clearCustomer}>Change</Button>
                    </Col>
                  </Row>
                </Form.Item>
              ) : (
                <>
                  <Form.Item label="Find a patient or customer (optional)">
                    <Row gutter={8} wrap={false}>
                      <Col flex="auto">
                        <PartySelect
                          value={partyValue}
                          onChange={setPartyValue}
                          onSelectParty={handlePartySelect}
                        />
                      </Col>
                      <Col flex="none">
                        <Button onClick={openCustomerCreate}>
                          + Create customer
                        </Button>
                      </Col>
                    </Row>
                  </Form.Item>
                  <Form.Item name="patientId" hidden>
                    <InputNumber />
                  </Form.Item>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        label="Patient name"
                        name="patientName"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Patient email"
                        name="patientEmail"
                        rules={[{ type: 'email' }]}
                      >
                        <Input placeholder="Needed to email the PDF" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item label="Billing address">
                        <Row gutter={8} wrap={false}>
                          <Col flex="auto">
                            <Form.Item name="billingAddressId" noStyle>
                              <Select
                                allowClear
                                placeholder={billingPlaceholder}
                                options={billingOptions}
                                disabled={!billingOptions.length}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="none">
                            <Button
                              onClick={() => setAddressModalOpen(true)}
                              disabled={!selectedPatientId}
                            >
                              + Add
                            </Button>
                          </Col>
                        </Row>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              <Typography.Title level={5}>Invoice</Typography.Title>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item label="Language" name="language">
                    <Select options={INVOICE_LANGUAGE_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Currency" name="currency">
                    {/* Platform bills EUR only; the BE fixes currency to EUR. */}
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Payment method" name="paymentMethod">
                    <Select options={INVOICE_PAYMENT_METHOD_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Tax mode" name="taxMode">
                    <Select options={INVOICE_TAX_MODE_OPTIONS} />
                  </Form.Item>
                </Col>
                {taxMode === InvoiceTaxMode.BaseOverride && (
                  <>
                    <Col span={8}>
                      <Form.Item label="Tax base amount" name="taxBaseAmount">
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Tax base rate (%)" name="taxBaseRate">
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
                <Col span={8}>
                  <Form.Item label="Due date" name="dueDate">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                {/*
                  Hidden on both saved-document modes. A draft is issued
                  from its own button, and a reissue always issues — the
                  replacement for a numbered document is not a draft.
                */}
                <Col
                  span={8}
                  style={{
                    display: isEditing || isReissue ? 'none' : undefined
                  }}
                >
                  <Form.Item
                    label="Issue now"
                    name="issue"
                    valuePropName="checked"
                    tooltip="Assigns a number, PDF, and (for Stripe) a payment link"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              {isReissue && (
                <Form.Item
                  label="Why is the original being voided"
                  name="reason"
                  tooltip="Goes on the original's audit trail. A void invoice keeps its number for ever, and whoever reads the sequence will ask what happened to it."
                >
                  <Input placeholder="e.g. Wrong consultation fee" />
                </Form.Item>
              )}

              <Typography.Title level={5}>Line items</Typography.Title>
              <LineItems
                products={products}
                onChanged={schedulePreview}
                reloadProducts={reloadProducts}
              />
            </Form>
          </Col>

          <Col xs={24} lg={10}>
            <div style={{ position: 'sticky', top: 16 }}>
              <Typography.Text type="secondary">
                Live preview {previewLoading ? '· updating…' : ''}
              </Typography.Text>
              <div
                style={{
                  border: '1px solid #eee',
                  borderRadius: 8,
                  height: '80vh',
                  marginTop: 8,
                  overflow: 'hidden',
                  background: '#fafafa'
                }}
              >
                {previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="Invoice preview"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                ) : (
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {previewLoading ? (
                      <Spin size="large" />
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Preview unavailable"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {selectedPatientId != null && (
        <AddressModal
          open={addressModalOpen}
          patientId={selectedPatientId}
          address={null}
          onClose={() => setAddressModalOpen(false)}
          onSuccess={handleAddressCreated}
        />
      )}

      <CustomerModal
        open={customerModalOpen}
        editing={customerEditing}
        onClose={closeCustomerModal}
        onSelect={handleCustomerSelected}
      />
    </PageWrapper>
  );
};

export default CreateInvoice;
